import Plotly from "plotly.js-dist-min";
import store from "@/store";
import { mergePlotlyTheme, plotlyThemeColors } from "@/utils/chartTheme";

export const PLOT_PNG_PAPER_BG_DARK = "#111827";
export const PLOT_PNG_PLOT_BG_DARK = "#374151";
export const PLOT_PNG_PAPER_BG_LIGHT = "#ffffff";
export const PLOT_PNG_PLOT_BG_LIGHT = "#ffffff";

export function plotlyPngBackgroundColors(isDarkMode) {
  return {
    paper_bgcolor: isDarkMode ? PLOT_PNG_PAPER_BG_DARK : PLOT_PNG_PAPER_BG_LIGHT,
    plot_bgcolor: isDarkMode ? PLOT_PNG_PLOT_BG_DARK : PLOT_PNG_PLOT_BG_LIGHT,
  };
}

export function resolveIsDarkMode() {
  if (typeof document !== "undefined") {
    const vApp = document.querySelector("#app .v-application");
    if (vApp?.classList.contains("dark-mode")) return true;
    if (vApp?.classList.contains("light-mode")) return false;
    if (document.body?.classList.contains("dark-mode")) return true;
    if (document.body?.classList.contains("light-mode")) return false;
  }
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem("darkMode") : null;
  if (stored === "true") return true;
  if (stored === "false") return false;
  try {
    return !!store.getters["darkMode/isDarkMode"];
  } catch {
    return false;
  }
}

function sanitizeFilenameBase(name) {
  return (
    String(name || "chart")
      .trim()
      .replace(/\.png$/i, "")
      .replace(/[^a-zA-Z0-9._-]+/g, "_")
      .replace(/^_+|_+$/g, "") || "chart"
  );
}

function resolvePlotlyElement(gd) {
  if (!gd || typeof gd.querySelector !== "function") return null;
  return gd.classList?.contains("js-plotly-plot") ? gd : gd.querySelector(".js-plotly-plot");
}

/**
 * Clone layout, apply theme colors, and use opaque backgrounds suitable for PNG export.
 * @param {object} layout
 * @param {boolean} isDarkMode
 * @param {string | object | null} titleOptions
 */
export function prepareLayoutForPngExport(layout, isDarkMode, titleOptions = null) {
  const base = layout && typeof layout === "object" ? JSON.parse(JSON.stringify(layout)) : {};
  const out = mergePlotlyTheme(base, isDarkMode);
  const bgs = plotlyPngBackgroundColors(isDarkMode);
  out.paper_bgcolor = bgs.paper_bgcolor;
  out.plot_bgcolor = bgs.plot_bgcolor;

  if (titleOptions != null) {
    const colors = plotlyThemeColors(isDarkMode);
    const text = typeof titleOptions === "string" ? titleOptions : titleOptions.text;
    const extra = typeof titleOptions === "object" && titleOptions !== null ? titleOptions : {};
    out.title = {
      x: 0.5,
      y: 0.95,
      ...extra,
      text: text ?? extra.text ?? "",
      font: {
        size: 16,
        color: colors.title,
        ...(extra.font || {}),
      },
    };
  }

  return out;
}

/**
 * Export traces + layout as PNG (off-screen render).
 */
export async function downloadPlotlyAsPng(traces, layout, filename, options = {}) {
  const {
    isDarkMode = resolveIsDarkMode(),
    title = null,
    width = 800,
    height = 600,
    layoutOverrides = null,
  } = options;

  let exportLayout = prepareLayoutForPngExport(layout, isDarkMode, title);
  if (layoutOverrides && typeof layoutOverrides === "object") {
    exportLayout = { ...exportLayout, ...layoutOverrides };
    if (layoutOverrides.margin) {
      exportLayout.margin = { ...(exportLayout.margin || {}), ...layoutOverrides.margin };
    }
  }
  exportLayout.width = width;
  exportLayout.height = height;
  exportLayout.autosize = false;

  const tempDiv = document.createElement("div");
  tempDiv.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:${width}px;height:${height}px;`;
  document.body.appendChild(tempDiv);

  const safeName = sanitizeFilenameBase(filename);

  try {
    await Plotly.newPlot(tempDiv, traces, exportLayout, {
      displayModeBar: false,
      responsive: false,
    });
    await Plotly.downloadImage(tempDiv, {
      format: "png",
      filename: safeName,
      width,
      height,
    });
    return true;
  } catch {
    return false;
  } finally {
    try {
      Plotly.purge(tempDiv);
    } catch {
      void 0;
    }
    if (tempDiv.parentNode) {
      tempDiv.parentNode.removeChild(tempDiv);
    }
  }
}

/**
 * Export an on-screen Plotly chart host element as PNG.
 */
export async function downloadPlotlyHostAsPng(gd, filenameBase, options = {}) {
  const plotEl = resolvePlotlyElement(gd);
  if (!plotEl?.data?.length) return false;

  const isDarkMode = options.isDarkMode ?? resolveIsDarkMode();
  const base = sanitizeFilenameBase(filenameBase);
  const w = Math.max(320, Math.round(plotEl.clientWidth || gd.clientWidth || gd.offsetWidth || 900));
  const h = Math.max(240, Math.round(plotEl.clientHeight || gd.clientHeight || gd.offsetHeight || 360));

  const snapshot = {
    data: JSON.parse(JSON.stringify(plotEl.data || [])),
    layout: prepareLayoutForPngExport(plotEl.layout || {}, isDarkMode),
  };
  snapshot.layout.width = w;
  snapshot.layout.height = h;
  snapshot.layout.autosize = false;

  const xAxisTitle = String(snapshot.layout?.xaxis?.title?.text ?? "").trim();
  if (xAxisTitle) {
    const margin = { ...(snapshot.layout.margin || {}) };
    margin.b = Math.max(Number(margin.b) || 0, 88);
    snapshot.layout.margin = margin;
  }

  const temp = document.createElement("div");
  temp.style.cssText = `position:fixed;left:-10000px;top:0;width:${w}px;height:${h}px;visibility:hidden;pointer-events:none;`;
  document.body.appendChild(temp);

  try {
    await Plotly.newPlot(temp, snapshot.data, snapshot.layout, {
      displayModeBar: false,
      responsive: false,
    });
    await Plotly.downloadImage(temp, {
      format: "png",
      filename: base,
      width: w,
      height: h,
    });
    return true;
  } catch {
    return false;
  } finally {
    try {
      Plotly.purge(temp);
    } catch {
      void 0;
    }
    temp.remove();
  }
}
